import { useState, useCallback } from 'react';
import { useMedicalClaimEOB } from '@sentara-health/sentara-api-hooks-core';
import { pdfConverter } from 'common';

/**
 * Custom hook for EOB PDF download
 * 
 * @param showMedical - Whether this is a medical claim (vs behavioral)
 * @param mId - Hashed mId from Members/Dependents API for EOB download
 * @returns {Object} Hook API
 * @returns {Function} Function to trigger EOB download
 * @returns {boolean} Loading state during download
 * @returns {boolean} hasError - Error state
 * @returns {Function} clearError - Function to clear error state
 */
export const useEobDownload = (showMedical: boolean, mId: string) => {
  const { MedicalClaimEOBType } = useMedicalClaimEOB();
  const [isDownloading, setIsDownloading] = useState(false);
  const [hasError, setHasError] = useState(false);

  /**
   * Validates if the API response contains valid PDF data
   * @param response - API response object
   * @returns true if valid, false otherwise
   */
  const isValidPdfResponse = (response: any): boolean => {
    // Check if response exists and has data property
    if (!response || typeof response !== 'object') {
      return false;
    }

    // Check for error indicators
    if (response.errorCode || response.error) {
      return false;
    }

    // Check if data is a non-empty string (base64 PDF)
    if (typeof response.data !== 'string' || response.data.trim() === '') {
      return false;
    }

    return true;
  };

  /**
   * Downloads EOB PDF for a given claim ID
   * @param claimId - The claim ID to download EOB for
   */
  const downloadEobPdf = useCallback(
    async (claimId: string) => {
      // Prevent duplicate downloads
      if (isDownloading) {
        return;
      }

      setIsDownloading(true);
      setHasError(false);

      try {
        const postData = {
          claimID: claimId,
          claimType: showMedical ? '0' : '1',
          memberId: mId,
        };

        const response = await MedicalClaimEOBType(postData);

        // Validate response contains valid PDF data
        if (!isValidPdfResponse(response)) {
          setHasError(true);
          return;
        }

        // Attempt to convert and download PDF
        try {
          pdfConverter((response as any).data, claimId);
        } catch (conversionError) {
          console.error('PDF conversion error:', conversionError);
          setHasError(true);
        }
      } catch (error) {
        console.error('EOB download error:', error);
        setHasError(true);
      } finally {
        setIsDownloading(false);
      }
    },
    [isDownloading, showMedical, mId, MedicalClaimEOBType]
  );

  /**
   * Clear the error state
   */
  const clearError = useCallback(() => {
    setHasError(false);
  }, []);

  return {
    downloadEobPdf,
    isDownloading,
    hasError,
    clearError,
  };
};
